import datacentral from '@/utils/axios/datacentral'
import qs from 'query-string'
import { SearchParams, SearchResponse, SearchSuggestion } from '../types'

/**
 * Global search API - tìm kiếm toàn cục
 */
export const globalSearch = async (
    params: SearchParams
): Promise<SearchResponse> => {
    const response = await datacentral.get(`/search?${qs.stringify(params)}`)
    return response?.data?.results
        ? response?.data
        : { results: [], total: 0, has_more: false }
}

/**
 * Get search suggestions based on query
 */
export const getSearchSuggestions = async (
    query: string
): Promise<SearchSuggestion[]> => {
    if (!query || query.length < 2) return []

    const response = await datacentral.get(
        `/search/suggestions?query=${encodeURIComponent(query)}`
    )
    const data = response?.data

    if (!data?.results) return []

    // Convert API response to frontend format
    return data.results.map((item: any) => ({
        text: item.suggestion,
        type: item.entity_type,
        count: item.total_frequency,
    }))
}

/**
 * Get popular searches
 */
export const getPopularSearches = async (): Promise<string[]> => {
    console.log('🌐 API Call: getPopularSearches')

    try {
        const response = await datacentral.get('/search/popular?limit=8')
        const data = response?.data

        console.log('✅ API Response: getPopularSearches', data)

        if (!data?.results) return []

        // Extract search queries from response
        return data.results.map((item: any) => item.search_query)
    } catch (error) {
        console.error('❌ API Error: getPopularSearches failed', error)
        return []
    }
}

/**
 * Get recent searches for current user
 */
export const getRecentSearches = async (): Promise<string[]> => {
    console.log('🌐 API Call: getRecentSearches')

    try {
        const response = await datacentral.get('/search/recent?limit=10')
        const data = response?.data

        console.log('✅ API Response: getRecentSearches', data)

        if (!data?.results) return []

        // Extract search queries from response
        return data.results.map((item: any) => item.search_query)
    } catch (error) {
        console.error(
            '❌ API Error: getRecentSearches failed, using localStorage fallback',
            error
        )

        // Fallback to localStorage
        try {
            const stored = localStorage.getItem('fallback_recent_searches')
            return stored ? JSON.parse(stored) : []
        } catch {
            return []
        }
    }
}

/**
 * Save search query to recent searches
 */
export const saveSearchQuery = async (
    query: string,
    scopes: string[] = [],
    customerIds: number[] = []
): Promise<void> => {
    console.log('🌐 API Call: saveSearchQuery', { query, scopes, customerIds })

    try {
        const response = await datacentral.post('/search/recent', {
            query,
            scopes,
            customer_ids: customerIds,
        })
        console.log('✅ API Response: saveSearchQuery success', response?.data)
    } catch (error) {
        console.error(
            '❌ API Error: saveSearchQuery failed, using localStorage fallback',
            error
        )

        // Fallback to localStorage
        try {
            const existing = localStorage.getItem('fallback_recent_searches')
            const recentSearches = existing ? JSON.parse(existing) : []

            // Add new search to beginning, remove duplicates, limit to 10
            const updated = [
                query,
                ...recentSearches.filter((q: string) => q !== query),
            ].slice(0, 10)

            localStorage.setItem(
                'fallback_recent_searches',
                JSON.stringify(updated)
            )
            console.log('✅ Fallback: Saved to localStorage', updated)
        } catch (fallbackError) {
            console.error(
                '❌ Fallback Error: localStorage save failed',
                fallbackError
            )
        }
    }
}

/**
 * Clear recent searches
 */
export const clearRecentSearches = async (): Promise<void> => {
    console.log('🌐 API Call: clearRecentSearches')

    try {
        await datacentral.delete('/search/recent')
        console.log('✅ API Response: clearRecentSearches success')
    } catch (error) {
        console.error(
            '❌ API Error: clearRecentSearches failed, using localStorage fallback',
            error
        )

        // Fallback to localStorage
        try {
            localStorage.removeItem('fallback_recent_searches')
            console.log('✅ Fallback: Cleared localStorage')
        } catch (fallbackError) {
            console.error(
                '❌ Fallback Error: localStorage clear failed',
                fallbackError
            )
        }
    }
}

/**
 * Refresh global search index (admin only)
 */
export const refreshGlobalSearchIndex = async (): Promise<void> => {
    await datacentral.post('/admin/refresh-global-search')
}
